import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { ExternalLink, MapPin, Star } from 'lucide-react';

// TripAdvisor brand owl green
const TA_GREEN = '#00aa6c';
const TA_PROFILE =
  'https://www.tripadvisor.com/Attraction_Review-g1096784-d34656188-Reviews-Motion_Reality_Sim_Racing_Ltd-Bromborough_Wirral_Merseyside_England.html';

function formatAddress(a) {
  if (!a) return '';
  const parts = [a.street1, a.street2, a.city, a.state || a.county, a.country].filter(Boolean);
  // Avoid repeating the city if it already appears in street1 (Bromborough case)
  return parts.join(', ').replace(/\s+/g, ' ').trim();
}

export default function TripAdvisorSummary() {
  // Fetch the canonical TripAdvisor URL + verified business address from the
  // Catalog API (with a verified fallback) via the backend function.
  const { data } = useQuery({
    queryKey: ['tripadvisor-catalog-location'],
    queryFn: () => base44.functions.invoke('tripadvisorCatalogLocation').then((r) => r.data),
  });

  const name = data?.name || 'Motion Reality Sim Racing Ltd';
  const profileUrl = data?.profile_url || TA_PROFILE;
  const address = data?.address || { street1: 'Bromborough', city: 'Wirral', state: 'Merseyside', country: 'England' };
  const rating = data?.rating || null;
  const reviewCount = data?.review_count || null;
  const addressLine = formatAddress(address);

  return (
    <section className="py-8 px-6 lg:px-10 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="bg-[#111115] border border-white/5 p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Small TripAdvisor logo mark */}
            <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="shrink-0" aria-label="TripAdvisor">
              <span
                className="block w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: TA_GREEN }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#111114" aria-hidden="true">
                  <path d="M12 9.5c-1.4 0-2.7.5-3.7 1.4l-.6-.6h-1.6l1.2 1.2c-.4.6-.6 1.3-.6 2 0 2 1.7 3.5 3.7 3.5 1.4 0 2.6-.7 3.2-1.8l.3.3.3-.3c.6 1.1 1.8 1.8 3.2 1.8 2 0 3.7-1.6 3.7-3.5 0-.7-.2-1.4-.6-2l1.2-1.2h-1.6l-.6.6c-1-.9-2.3-1.4-3.7-1.4zm-2.4 5.6c-.9 0-1.6-.7-1.6-1.6s.7-1.6 1.6-1.6 1.6.7 1.6 1.6-.7 1.6-1.6 1.6zm4.8 0c-.9 0-1.6-.7-1.6-1.6s.7-1.6 1.6-1.6 1.6.7 1.6 1.6-.7 1.6-1.6 1.6z" />
                </svg>
              </span>
            </a>

            <div className="min-w-0">
              <p className="text-[9px] font-bold tracking-widest mb-0.5" style={{ color: TA_GREEN }}>TRIPADVISOR</p>
              <h3 className="text-white font-bold text-sm uppercase leading-none truncate">{name}</h3>
              {addressLine && (
                <p className="text-white/40 text-xs flex items-center gap-1 mt-1 truncate">
                  <MapPin className="w-3 h-3 shrink-0" style={{ color: TA_GREEN }} />
                  <span className="truncate">{addressLine}</span>
                </p>
              )}
            </div>

            {rating != null && (
              <div className="flex items-center gap-1.5 shrink-0 md:ml-2">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-3 h-3 ${n <= Math.round(rating) ? 'text-[#00aa6c] fill-[#00aa6c]' : 'text-white/15'}`} />
                  ))}
                </div>
                <span className="text-white/40 text-[11px]">
                  {Number(rating).toFixed(1)}{reviewCount != null ? ` · ${reviewCount}` : ''}
                </span>
              </div>
            )}
          </div>

          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-bold text-[11px] tracking-widest px-4 py-2 transition-colors shrink-0 self-start md:self-auto"
            style={{ background: TA_GREEN, color: '#fff' }}
          >
            READ REVIEWS <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </section>
  );
}